import type { ApiClient, Schemas, paths } from '@autho/api-client';
import { createApiClient, isApiError } from '@autho/api-client';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { createContext, useContext } from 'react';

export type EntitySummary = Schemas['EntitySummary'];
export type EntityDetail = Schemas['EntityDetail'];
export type Provenance = Schemas['Provenance'];
export type ClarifyItem = Schemas['ClarifyItem'];
export type CompanyAsset = Schemas['CompanyAsset'];
export type RegisterView = Schemas['RegisterView'];
export type RegisterHolding = Schemas['RegisterHolding'];
export type RegisterRowInput = Schemas['RegisterRowInput'];
export type Belief = Schemas['Belief'];
export type PolicyView = Schemas['PolicyView'];
export type Approval = Schemas['Approval'];
export type AuditEvent = Schemas['AuditEvent'];
export type Connection = Schemas['Connection'];
export type Membership = Schemas['Membership'];
export type LedgerRuns = Schemas['LedgerRuns'];
export type LedgerRun = Schemas['LedgerRun'];
export type LedgerBoundary = NonNullable<Schemas['LedgerBoundary']>;
export type RecordTable = Provenance['table'];
/** Where the move of one personal account to its person's own profile stands (R2). */
export type MoveStatus = Schemas['MoveStatus'];
/**
 * The resources a grant may name, taken from the API's own request body rather than listed again. It was
 * listed again, and it stayed at M1.5's three words while the engine's vocabulary grew by three (F69).
 */
export type GrantResources = NonNullable<
  paths['/api/t/agents/{id}/grants']['post']['requestBody']
>['content']['application/json']['resources'];

/** An API error with its stable code, so screens can say exactly what went wrong. */
export class ApiRequestError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
  ) {
    super(message);
  }
}

export function createClient(
  getToken: () => Promise<string | null>,
  getTenantId: () => string | null,
) {
  return createApiClient({ getToken, getTenantId });
}

export const ApiContext = createContext<ApiClient | null>(null);

export function useApi(): ApiClient {
  const api = useContext(ApiContext);
  if (!api) throw new Error('useApi outside ApiContext');
  return api;
}

/**
 * The failure a mutation throws, built from what the server actually said (F202).
 *
 * Three hooks built theirs from `response.statusText` and a code of their own invention. Over HTTP/2 —
 * which Cloud Run negotiates — `statusText` is **the empty string**, so the alert rendered blank: a red
 * box with nothing in it, on a screen whose whole job at that moment is to say what went wrong. And the
 * server's own `{ error: { code, message } }`, written for the person, was thrown away to make it.
 *
 * `whenFallback` is the sentence to use when the body carried none — never a bare status, and never the
 * empty string.
 */
async function failure(
  response: Response,
  error: unknown,
  whenFallback: string,
): Promise<ApiRequestError> {
  if (isApiError(error))
    return new ApiRequestError(response.status, error.error.code, error.error.message);
  // A 204 and a 404 both arrive with no body worth reading; so does a proxy's own error page.
  const body = await response
    .clone()
    .json()
    .catch(() => null);
  if (isApiError(body))
    return new ApiRequestError(response.status, body.error.code, body.error.message);
  return new ApiRequestError(response.status, whenFallback, response.statusText || whenFallback);
}

async function unwrap<T>(
  promise: Promise<{ data?: T; error?: unknown; response: Response }>,
): Promise<T> {
  const { data, error, response } = await promise;
  if (error !== undefined || data === undefined) {
    if (isApiError(error))
      throw new ApiRequestError(response.status, error.error.code, error.error.message);
    // `statusText` is empty over HTTP/2, so it is never the message on its own.
    throw new ApiRequestError(response.status, 'unknown', response.statusText || 'unknown');
  }
  return data;
}

/**
 * **Every tenant-scoped key starts with the literal `'t'` and then the tenant id**, because that prefix is what
 * `useInvalidateTenant` invalidates — `queryKey: ['t', tenantId]`.
 *
 * Five keys here did not. They were written `[t, 'bank-app']`, where `t` is the *tenant id*: a key that looks
 * right, type-checks, caches correctly and is **never invalidated by anything**. The screens then served answers
 * from before the mutation that changed them: paste the Enable Banking application id and the Bank page goes on
 * saying *paste it under Settings first*, which is what the end-to-end run caught the first time it ran on the
 * stack. Four were fixed with that run; `group` was the fifth, and it never shipped (the key left with its page in R4).
 * `every-query-key-can-be-invalidated.test.ts` asserts the shape of every entry by calling it, so the next one
 * cannot be written this way.
 */
export const keys = {
  meEids: (t: string) => ['t', t, 'me-eids'] as const,
  shared: (t: string) => ['t', t, 'shared-with-companies'] as const,
  entityBooks: (t: string, id: string) => ['t', t, 'entity-books', id] as const,
  /** Under the books' own key, so whatever refreshes the books after an upload refreshes the comparison too. */
  entityStandardChart: (t: string, id: string, asOf: string) =>
    ['t', t, 'entity-books', id, 'standard-chart', asOf] as const,
  bankApp: (t: string) => ['t', t, 'bank-app'] as const,
  banks: (t: string) => ['t', t, 'banks'] as const,
  bankFeed: (t: string) => ['t', t, 'bank-feed'] as const,
  bankLive: (t: string, entity: string | null) => ['t', t, 'bank-live', entity] as const,
  me: ['me'] as const,
  /** The per-person consent screen (D85): one answer across every profile the person has, so no tenant. */
  oidcQuestion: (t: string, uid: string) => ['t', t, 'oidc', uid] as const,
  mcpConsent: (clientId: string, redirectUri: string) =>
    ['mcp-consent', clientId, redirectUri] as const,
  entities: (tenantId: string) => ['t', tenantId, 'entities'] as const,
  assets: (tenantId: string) => ['t', tenantId, 'assets'] as const,
  register: (tenantId: string) => ['t', tenantId, 'register'] as const,
  entity: (tenantId: string, id: string) => ['t', tenantId, 'entity', id] as const,
  provenance: (tenantId: string, table: string, id: string) =>
    ['t', tenantId, 'provenance', table, id] as const,
  clarify: (tenantId: string, status: string) => ['t', tenantId, 'clarify', status] as const,
  documents: (tenantId: string) => ['t', tenantId, 'documents'] as const,
  audit: (tenantId: string, filters: object) => ['t', tenantId, 'audit', filters] as const,
  auditVerify: (tenantId: string) => ['t', tenantId, 'audit-verify'] as const,
  connections: (tenantId: string) => ['t', tenantId, 'connections'] as const,
  bank: (tenantId: string) => ['t', tenantId, 'bank'] as const,
  attention: (tenantId: string) => ['t', tenantId, 'attention'] as const,
  // Under `attention`, so whatever refreshes today's list refreshes *Older* with it (F516).
  attentionOlder: (tenantId: string, query: string) =>
    ['t', tenantId, 'attention', 'older', query] as const,
  people: (tenantId: string, query: string) => ['t', tenantId, 'people', query] as const,
  meProfile: (tenantId: string) => ['t', tenantId, 'me-profile'] as const,
  holdings: (tenantId: string) => ['t', tenantId, 'holdings'] as const,
  goals: (tenantId: string) => ['t', tenantId, 'goals'] as const,
  meBank: (tenantId: string) => ['t', tenantId, 'me-bank'] as const,
  meContacts: (tenantId: string) => ['t', tenantId, 'me-contacts'] as const,
  personCard: (tenantId: string, id: string) => ['t', tenantId, 'person-card', id] as const,
  meBanks: (tenantId: string) => ['t', tenantId, 'me-banks'] as const,
  meTasks: (tenantId: string) => ['t', tenantId, 'me-tasks'] as const,
  commitments: (tenantId: string, state: string) => ['t', tenantId, 'commitments', state] as const,
  ledgerRuns: (tenantId: string, entityId: string) =>
    ['t', tenantId, 'ledger-runs', entityId] as const,
  agents: (tenantId: string) => ['t', tenantId, 'agents'] as const,
  /** The kill switch and the rules (F369). */
  policy: (tenantId: string) => ['t', tenantId, 'policy'] as const,
  approvals: (tenantId: string) => ['t', tenantId, 'approvals'] as const,
  beliefs: (tenantId: string, entityId: string, includeForgotten: boolean) =>
    ['t', tenantId, 'beliefs', entityId, includeForgotten] as const,
  counterparties: (tenantId: string, entityId: string) =>
    ['t', tenantId, 'counterparties', entityId] as const,
  cashForecast: (tenantId: string, entityId: string) =>
    ['t', tenantId, 'cash-forecast', entityId] as const,
  engine: (tenantId: string) => ['t', tenantId, 'engine'] as const,
  chatThreads: (tenantId: string) => ['t', tenantId, 'chat', 'threads'] as const,
  chatThread: (tenantId: string, threadId: string) =>
    ['t', tenantId, 'chat', 'thread', threadId] as const,
  chatTurn: (tenantId: string, threadId: string) =>
    ['t', tenantId, 'chat', 'turn', threadId] as const,
  ownKey: (tenantId: string) => ['t', tenantId, 'engine', 'key'] as const,
  accounts: (tenantId: string) => ['t', tenantId, 'accounts'] as const,
  /** One record screen's pages (Personal step 2), per surface and account filter. */
  records: (tenantId: string, surface: string, account: string | null) =>
    ['t', tenantId, 'records', surface, account] as const,
  accountCalendars: (tenantId: string, id: string) =>
    ['t', tenantId, 'account-calendars', id] as const,
  accountAddresses: (tenantId: string, id: string) =>
    ['t', tenantId, 'account-addresses', id] as const,
  move: (tenantId: string, id: string) => ['t', tenantId, 'move', id] as const,
};

/**
 * Who is signed in, and every profile they belong to. **Every login has a Me** (R1, D81): the server makes it on
 * this request the first time, in the language the screen is in (F163). The locale travels with the request and
 * stays out of the key: the answer is the same in either language, and a key that changed with the language
 * would put the whole app back behind the loading screen each time somebody switched it.
 */
export function useMe(enabled: boolean, locale: 'en' | 'nb-NO') {
  const api = useApi();
  return useQuery({
    queryKey: keys.me,
    enabled,
    queryFn: () => unwrap(api.GET('/api/me', { params: { query: { locale } } })),
  });
}

/**
 * **An Org is made from its organisation number, and the person making it becomes its admin** (R1, A3, A4; D82,
 * D83). The failure keeps the server's code, because each refusal has its own sentence on the screen. The profile
 * list is refetched before this settles, so a caller that switches to the new Org finds it there — switching
 * first would fall back to the first profile in the list and show another company's screens.
 */
export function useCreateOrg() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (body: { orgnr: string; locale: 'en' | 'nb-NO' }) => {
      const { data, error, response } = await api.POST('/api/tenants', { body });
      // `unknown` when no body came back (a proxy's own error page): it is the code the trouble table reads as
      // *no sentence of its own*, so the status decides what is said rather than a code shown as if it were one.
      if (error !== undefined || data === undefined)
        throw await failure(response, error, 'unknown');
      return data;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.me }),
  });
}

/**
 * The company's legal entities. **Asked only in a company's profile** (R3): a personal profile holds none, and the
 * route answers it `409 wrong_profile`, so a screen drawn in either kind passes whether the open one is a company.
 */
export function useEntities(tenantId: string, inCompany = true) {
  const api = useApi();
  return useQuery({
    queryKey: keys.entities(tenantId),
    queryFn: async () => (await unwrap(api.GET('/api/t/entities'))).entities,
    enabled: inCompany,
  });
}

export function useEntity(tenantId: string, id: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.entity(tenantId, id),
    queryFn: () => unwrap(api.GET('/api/t/entities/{id}', { params: { path: { id } } })),
  });
}

function useInvalidateTenant(tenantId: string) {
  const qc = useQueryClient();
  return () => qc.invalidateQueries({ queryKey: ['t', tenantId] });
}

export function useAddEntity(tenantId: string) {
  const api = useApi();
  const invalidate = useInvalidateTenant(tenantId);
  return useMutation({
    mutationFn: (body: { orgnr: string; state: 'managed' | 'external' }) =>
      unwrap(api.POST('/api/t/entities', { body })),
    onSuccess: invalidate,
  });
}

/** What this person shares with the companies they belong to (R3b, 0129), from their Personal profile. */
export function useSharedWithCompanies(tenantId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.shared(tenantId),
    queryFn: () => unwrap(api.GET('/api/t/me/federated-grants')),
  });
}

/** Shares accounts with a company, replacing the open grant to it. */
export function useShareWithCompany(tenantId: string) {
  const api = useApi();
  const invalidate = useInvalidateTenant(tenantId);
  return useMutation({
    mutationFn: (body: {
      orgId: string;
      accounts: string[];
      resources: ('me.mail' | 'me.calendar' | 'me.drive')[];
      purpose: string;
      days: number;
    }) => unwrap(api.POST('/api/t/me/federated-grants', { body })),
    onSuccess: invalidate,
  });
}

/** Revokes a grant: the next read under it is refused. */
export function useRevokeShare(tenantId: string) {
  const api = useApi();
  const invalidate = useInvalidateTenant(tenantId);
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(api.POST('/api/t/me/federated-grants/{id}/revoke', { params: { path: { id } } })),
    onSuccess: invalidate,
  });
}

/**
 * **Sign in with Autho's question** (F494, F508): which app asks to sign the person in, read from their Personal
 * profile with this browser's sign-in cookie. Never retried: a sign-in that is gone stays gone.
 */
export function useOidcQuestion(tenantId: string, uid: string, enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: keys.oidcQuestion(tenantId, uid),
    enabled,
    retry: false,
    queryFn: () => unwrap(api.GET('/api/t/me/oidc/{uid}', { params: { path: { uid } } })),
  });
}

/** The person's answer; the API says where the browser goes next. */
export function useOidcAnswer() {
  const api = useApi();
  return useMutation({
    mutationFn: ({ uid, decision }: { uid: string; decision: 'allow' | 'deny' }) =>
      unwrap(api.POST('/api/t/me/oidc/{uid}', { params: { path: { uid } }, body: { decision } })),
  });
}

/** The person's eIDs as ways in (0127), from their Personal profile. */
export function useMeEids(tenantId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.meEids(tenantId),
    queryFn: () => unwrap(api.GET('/api/t/me/eid')),
  });
}

/** Where to send the person to add an eID; the flow itself is sealed in a cookie the API sets. */
export function useStartEidLink() {
  const api = useApi();
  return useMutation({
    mutationFn: (provider: 'vipps' | 'bankid') =>
      unwrap(api.GET('/api/t/me/eid/{provider}/start', { params: { path: { provider } } })),
  });
}

/** Hands the provider's answer back to add the eID to the person signed in. */
export function useCompleteEidLink(tenantId: string) {
  const api = useApi();
  const invalidate = useInvalidateTenant(tenantId);
  return useMutation({
    mutationFn: ({
      provider,
      ...body
    }: {
      provider: 'vipps' | 'bankid';
      code?: string;
      state?: string;
      error?: string;
    }) =>
      unwrap(api.POST('/api/t/me/eid/{provider}/link', { params: { path: { provider } }, body })),
    onSuccess: invalidate,
  });
}

/**
 * **Finishing a BankID sign-up** (D51 row 8): the email link has signed the person in, and the eID waiting in this
 * browser's sealed cookie is added to that account. The person's profiles are read again: one may have been made.
 */
export function useFinishEidSignUp() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(api.POST('/api/eid/sign-up')),
    onSuccess: () => qc.invalidateQueries(),
  });
}

export function useRemoveEid(tenantId: string) {
  const api = useApi();
  const invalidate = useInvalidateTenant(tenantId);
  return useMutation({
    mutationFn: (provider: 'vipps' | 'bankid') =>
      unwrap(api.DELETE('/api/t/me/eid/{provider}', { params: { path: { provider } } })),
    onSuccess: invalidate,
  });
}

export function useAssets(tenantId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.assets(tenantId),
    queryFn: async () => (await unwrap(api.GET('/api/t/assets'))).assets,
  });
}

type AssetFields = { stake?: string | null; notes?: string | null };

export function useAddAsset(tenantId: string) {
  const api = useApi();
  const invalidate = useInvalidateTenant(tenantId);
  return useMutation({
    mutationFn: (
      body: AssetFields & ({ entityId: string } | { orgnr: string; state: 'managed' | 'manual' }),
    ) => unwrap(api.POST('/api/t/assets', { body })),
    onSuccess: invalidate,
  });
}

export function useEditAsset(tenantId: string) {
  const api = useApi();
  const invalidate = useInvalidateTenant(tenantId);
  return useMutation({
    mutationFn: ({ entityId, ...body }: AssetFields & { entityId: string }) =>
      unwrap(api.PATCH('/api/t/assets/{entityId}', { params: { path: { entityId } }, body })),
    onSuccess: invalidate,
  });
}

export function useEndAsset(tenantId: string) {
  const api = useApi();
  const invalidate = useInvalidateTenant(tenantId);
  return useMutation({
    mutationFn: ({
      entityId,
      ...body
    }: {
      entityId: string;
      reason: 'sold' | 'dissolved';
      endedOn: string;
    }) =>
      unwrap(api.POST('/api/t/assets/{entityId}/end', { params: { path: { entityId } }, body })),
    onSuccess: invalidate,
  });
}

/** Entered by mistake: the asset is removed, and the audit log keeps what it said. */
export function useRemoveAsset(tenantId: string) {
  const api = useApi();
  const invalidate = useInvalidateTenant(tenantId);
  return useMutation({
    mutationFn: (entityId: string) =>
      unwrap(api.DELETE('/api/t/assets/{entityId}', { params: { path: { entityId } } })),
    onSuccess: invalidate,
  });
}

/** The newest year of Skatteetaten's shareholder register that this Org has imported (R6). */
export function useRegister(tenantId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.register(tenantId),
    queryFn: async () => unwrap(api.GET('/api/t/register')),
  });
}

/** One register year, as the rows the browser kept from the file, with its counts of what it dropped. */
export function useImportRegister(tenantId: string) {
  const api = useApi();
  const invalidate = useInvalidateTenant(tenantId);
  return useMutation({
    mutationFn: (body: {
      registerYear: number;
      rows: RegisterRowInput[];
      dropped: {
        person: number;
        foreign: number;
        invalid_orgnr: number;
        malformed: number;
        not_the_org: number;
      };
    }) => unwrap(api.POST('/api/t/register/imports', { body })),
    onSuccess: invalidate,
  });
}

/** Clarify's question about a stake the register disagrees with: the typed one is right, or the register's. */
export function useAnswerRegisterStake(tenantId: string) {
  const api = useApi();
  const invalidate = useInvalidateTenant(tenantId);
  return useMutation({
    mutationFn: ({
      entityId,
      ...body
    }: {
      entityId: string;
      registerYear: number;
      answer: 'typed' | 'register';
    }) =>
      unwrap(
        api.POST('/api/t/register/stake/{entityId}', { params: { path: { entityId } }, body }),
      ),
    onSuccess: invalidate,
  });
}

/** Clarify's question about a company already here, answered *not an asset*. */
export function useNotAnAsset(tenantId: string) {
  const api = useApi();
  const invalidate = useInvalidateTenant(tenantId);
  return useMutation({
    mutationFn: (entityId: string) =>
      unwrap(api.POST('/api/t/assets/{entityId}/not-an-asset', { params: { path: { entityId } } })),
    onSuccess: invalidate,
  });
}

export function useSetEntityState(tenantId: string) {
  const api = useApi();
  const invalidate = useInvalidateTenant(tenantId);
  return useMutation({
    mutationFn: ({ id, state }: { id: string; state: 'managed' | 'linked' | 'external' }) =>
      unwrap(api.PUT('/api/t/entities/{id}/state', { params: { path: { id } }, body: { state } })),
    onSuccess: invalidate,
  });
}

export function useRefreshEntity(tenantId: string) {
  const api = useApi();
  const invalidate = useInvalidateTenant(tenantId);
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(api.POST('/api/t/entities/{id}/sync', { params: { path: { id } } })),
    onSuccess: invalidate,
  });
}

export function useProvenance(tenantId: string, table: RecordTable | null, id: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: keys.provenance(tenantId, table ?? '', id ?? ''),
    enabled: Boolean(table && id),
    queryFn: () =>
      unwrap(
        api.GET('/api/t/records/{table}/{id}/provenance', {
          params: { path: { table: table as RecordTable, id: id as string } },
        }),
      ),
  });
}

export function useClarify(tenantId: string, status: 'open' | 'resolved' | 'skipped' = 'open') {
  const api = useApi();
  return useQuery({
    queryKey: keys.clarify(tenantId, status),
    queryFn: () => unwrap(api.GET('/api/t/clarify', { params: { query: { status } } })),
    // This queue is the one list that changes without the person doing anything: extraction runs in the
    // background, and a document that fails while they have Autho open would otherwise sit unseen until they
    // happened to reload. A failure nobody sees is the same as data that never existed, so the queue refetches
    // when the window regains focus and on a slow interval besides. `staleTime: 0` because the shared default
    // of 15 seconds would let a focus land inside the window and return the cache instead.
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
  });
}

export function useAccounts(tenantId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.accounts(tenantId),
    queryFn: () => unwrap(api.GET('/api/t/accounts')),
    // A connection goes stale without anybody clicking, and an expiry arrives on a clock.
    // `staleTime: 0` because Structure and Relationships ask for this list too, to say a paused source there
    // (F374): with the shared 15 seconds, Sources opened straight after them showed that read, not this one.
    staleTime: 0,
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
  });
}

// No tenant argument: starting a connection reads and invalidates nothing, and the tenant already travels
// as a header. A parameter kept for symmetry is a parameter a caller will one day believe does something.
export function useStartGoogleConnect() {
  const api = useApi();
  return useMutation({
    mutationFn: (body: {
      surface: 'mail' | 'calendar' | 'drive' | 'contacts';
      reconnecting?: string;
    }) => unwrap(api.POST('/api/t/connections/google/authorize', { body })),
  });
}

export function useCompleteGoogleConnect(tenantId: string) {
  const api = useApi();
  const invalidate = useInvalidateTenant(tenantId);
  return useMutation({
    mutationFn: (body: {
      code?: string;
      state?: string;
      error?: string;
      connectDifferentAccount?: boolean;
    }) => unwrap(api.POST('/api/t/connections/google/callback', { body })),
    onSuccess: invalidate,
  });
}

/**
 * What the first read of an account would cost. A POST because it calls the provider to count and sample —
 * it changes nothing, but it is not free, and a GET that costs a round trip to Google is a GET something
 * will one day prefetch.
 */
// No tenant argument: an estimate reads and invalidates nothing, and the tenant already travels as a
// header. A parameter kept for symmetry is one a caller will one day believe does something.
export function useEstimateSync() {
  const api = useApi();
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(api.POST('/api/t/connections/{id}/estimate', { params: { path: { id } } })),
  });
}

/**
 * The calendars of one connected account, and the decision about each (F303).
 *
 * A read of Autho's own record rather than of Google: the list is written down by whichever pass last read
 * the account, so this answers instantly and says `listed: false` when no read has happened yet.
 */
/** Todoist in the product (D57): the registration's status, the paste, the connect, the callback. */
export function useTodoistApp(tenantId: string) {
  const api = useApi();
  return useQuery({
    queryKey: ['t', tenantId, 'provider-app', 'todoist'],
    queryFn: () => unwrap(api.GET('/api/t/provider-apps/todoist')),
  });
}

export function useSaveTodoistApp(tenantId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: { clientId: string; clientSecret: string }) =>
      unwrap(api.PUT('/api/t/provider-apps/todoist', { body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['t', tenantId, 'provider-app', 'todoist'] }),
  });
}

export function useStartTodoistConnect() {
  const api = useApi();
  return useMutation({
    mutationFn: () => unwrap(api.POST('/api/t/connections/todoist/authorize')),
  });
}

export function useCompleteTodoistConnect(tenantId: string) {
  const api = useApi();
  const invalidate = useInvalidateTenant(tenantId);
  return useMutation({
    mutationFn: (body: { code?: string; state?: string; error?: string }) =>
      unwrap(api.POST('/api/t/connections/todoist/callback', { body })),
    onSuccess: invalidate,
  });
}

export function useEntityBooks(tenantId: string, entityId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.entityBooks(tenantId, entityId),
    queryFn: () =>
      unwrap(api.GET('/api/t/entities/{id}/books', { params: { path: { id: entityId } } })),
  });
}

/**
 * **The standard chart, beside the totals** (I-23): the balances at a date by each account's own number and by the
 * standard account its SAF-T file maps it to. A company with no books for the date is a 404, which is nothing to
 * show rather than an error, so it is answered as null.
 */
export function useEntityStandardChart(tenantId: string, entityId: string, asOf: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.entityStandardChart(tenantId, entityId, asOf),
    queryFn: async () => {
      try {
        return await unwrap(
          api.GET('/api/t/entities/{id}/books/standard-chart', {
            params: { path: { id: entityId }, query: { asOf } },
          }),
        );
      } catch (error) {
        if (error instanceof ApiRequestError && error.status === 404) return null;
        throw error;
      }
    },
  });
}

export function useBankApp(tenantId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.bankApp(tenantId),
    queryFn: () => unwrap(api.GET('/api/t/provider-apps/enablebanking')),
  });
}

export function useSaveBankApp(tenantId: string) {
  const api = useApi();
  const invalidate = useInvalidateTenant(tenantId);
  return useMutation({
    mutationFn: (body: { applicationId: string }) =>
      unwrap(api.PUT('/api/t/provider-apps/enablebanking', { body })),
    onSuccess: invalidate,
  });
}

/** Read live from the provider when the picker opens — a bank list is nothing worth caching for long. */
export function useBanks(tenantId: string, enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: keys.banks(tenantId),
    enabled,
    staleTime: 5 * 60_000,
    queryFn: () => unwrap(api.GET('/api/t/connections/bank/banks')),
  });
}

export function useStartBankConnect() {
  const api = useApi();
  return useMutation({
    mutationFn: (body: { entityId: string; bank: string }) =>
      unwrap(api.POST('/api/t/connections/bank/authorize', { body })),
  });
}

export function useCompleteBankConnect(tenantId: string) {
  const api = useApi();
  const invalidate = useInvalidateTenant(tenantId);
  return useMutation({
    mutationFn: (body: { code?: string; state?: string; error?: string }) =>
      unwrap(api.POST('/api/t/connections/bank/callback', { body })),
    onSuccess: invalidate,
  });
}

export function useBankFeed(tenantId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.bankFeed(tenantId),
    queryFn: () => unwrap(api.GET('/api/t/bank/feed')),
  });
}

/**
 * **The live balance, read when the screen opens** (list B item 4). The owner is present, which is what makes
 * this read attended at the bank; the server caches it for a few minutes, and `staleTime` here keeps a screen
 * that re-renders from asking again inside that window. Nothing is stored either side.
 */
export function useBankLive(tenantId: string, entity: string | null, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: keys.bankLive(tenantId, entity),
    queryFn: () =>
      unwrap(api.GET('/api/t/bank/live', entity === null ? {} : { params: { query: { entity } } })),
    enabled,
    staleTime: 60_000,
  });
}

export function useAccountCalendars(tenantId: string, id: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: keys.accountCalendars(tenantId, id ?? ''),
    enabled: id !== null,
    queryFn: () =>
      unwrap(
        api.GET('/api/t/connections/{id}/calendars', { params: { path: { id: id as string } } }),
      ),
  });
}

/**
 * A decision about one calendar. The id goes in the body and never in the path: for Google it is the address
 * the calendar belongs to, and a request line reaches every access log (G29, G33).
 */
/**
 * Which addresses are this person, for one account (F307). The candidates are read off the calendars the
 * account lists; none of them counts until somebody answers.
 */
export function useAccountAddresses(tenantId: string, id: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: keys.accountAddresses(tenantId, id ?? ''),
    enabled: id !== null,
    queryFn: () =>
      unwrap(
        api.GET('/api/t/connections/{id}/addresses', { params: { path: { id: id as string } } }),
      ),
  });
}

/** *This is me* or *this is not me*, about one address. In the body, never the path (G29, G33). */
export function useAnswerAddress() {
  const api = useApi();
  return useMutation({
    // A plain send, as the calendar choice is (F310): the list's own overlay knows which answer is newest.
    mutationFn: ({
      id,
      address,
      verdict,
      suggestedFrom,
    }: {
      id: string;
      address: string;
      verdict: 'mine' | 'not_mine';
      suggestedFrom: 'calendar' | 'typed';
    }) =>
      unwrap(
        api.POST('/api/t/connections/{id}/addresses/verdict', {
          params: { path: { id } },
          body: { address, verdict, suggestedFrom },
        }),
      ),
  });
}

/** The one write the page makes to the addresses cache: one address moved between the three lists. */
export function useAddressListCache(tenantId: string) {
  const qc = useQueryClient();
  return (accountId: string, address: string, verdict: 'mine' | 'not_mine') =>
    qc.setQueryData(
      keys.accountAddresses(tenantId, accountId),
      (old: { mine: string[]; notMine: string[]; candidates: string[] } | undefined) => {
        if (old === undefined) return old;
        const out = {
          ...old,
          mine: old.mine.filter((a) => a !== address),
          notMine: old.notMine.filter((a) => a !== address),
          candidates: old.candidates.filter((a) => a !== address),
        };
        if (verdict === 'mine') out.mine = [...out.mine, address].sort();
        else out.notMine = [...out.notMine, address].sort();
        return out;
      },
    );
}

export function useChooseCalendar() {
  const api = useApi();
  return useMutation({
    /**
     * A plain send (F310). The cache correction lives with the caller, because only the caller knows which
     * click is newest: a global onSuccess here let whichever response landed last write the screen, which
     * is exactly the overwrite the row's sequence number exists to prevent.
     */
    mutationFn: ({
      id,
      calendarId,
      choice,
    }: {
      id: string;
      calendarId: string;
      choice: 'read' | 'ignore' | 'attendees';
    }) =>
      unwrap(
        api.POST('/api/t/connections/{id}/calendars/choice', {
          params: { path: { id } },
          body: { calendarId, choice },
        }),
      ),
  });
}

/** The one write the page makes to the calendars cache: one row, corrected in place, never a refetch. */
export function useCalendarRowCache(tenantId: string) {
  const qc = useQueryClient();
  return (
    accountId: string,
    calendarId: string,
    patch: Partial<{
      decided: 'read' | 'ignore' | 'attendees';
      decidedAt: string;
      effective: 'read' | 'ignore' | 'attendees';
      applyingSince: string | null;
    }>,
  ) =>
    qc.setQueryData(
      keys.accountCalendars(tenantId, accountId),
      (old: { calendars: ({ id: string } & Record<string, unknown>)[] } | undefined) =>
        old === undefined
          ? old
          : {
              ...old,
              calendars: old.calendars.map((c) => (c.id === calendarId ? { ...c, ...patch } : c)),
            },
    );
}

/** The second beat: tombstoning and re-deriving, fired right after a choice lands (F310). */
export function useApplyCalendar() {
  const api = useApi();
  return useMutation({
    mutationFn: ({ id, calendarId }: { id: string; calendarId: string }) =>
      unwrap(
        api.POST('/api/t/connections/{id}/calendars/apply', {
          params: { path: { id } },
          body: { calendarId },
        }),
      ),
  });
}

export function useApproveSync(tenantId: string) {
  const api = useApi();
  const invalidate = useInvalidateTenant(tenantId);
  return useMutation({
    mutationFn: ({ id, estimatedNok }: { id: string; estimatedNok: number }) =>
      unwrap(
        api.POST('/api/t/connections/{id}/approve-sync', {
          params: { path: { id } },
          body: { estimatedNok },
        }),
      ),
    onSuccess: invalidate,
  });
}

/**
 * Connecting iCloud. The password goes straight from the field into the request body and is held nowhere
 * else — not in a query cache, not in a mutation's `variables` that a devtools panel would show, because
 * the caller clears the field the moment this resolves (D45).
 */
export function useConnectICloud(tenantId: string) {
  const api = useApi();
  const invalidate = useInvalidateTenant(tenantId);
  return useMutation({
    mutationFn: (body: { address: string; appPassword: string; contacts?: boolean }) =>
      unwrap(api.POST('/api/t/connections/icloud', { body })),
    onSuccess: invalidate,
  });
}

export function useDisconnect(tenantId: string) {
  const api = useApi();
  const invalidate = useInvalidateTenant(tenantId);
  return useMutation({
    mutationFn: ({ id, basis, forget }: { id: string; basis: string; forget?: boolean }) =>
      unwrap(
        api.DELETE('/api/t/connections/{id}', {
          params: { path: { id } },
          body: { basis, ...(forget === undefined ? {} : { forget }) },
        }),
      ),
    onSuccess: invalidate,
  });
}

/**
 * **Pause or resume a source** (Sources, F374): Autho stops reading it on a schedule and changes nothing at the
 * provider, or reads it again from where it stopped. Every list that shows a source is refreshed, because the
 * screens of the categories it feeds say *paused* too.
 */
export function useSetPaused(tenantId: string) {
  const api = useApi();
  const invalidate = useInvalidateTenant(tenantId);
  return useMutation({
    mutationFn: ({ id, paused }: { id: string; paused: boolean }) =>
      unwrap(
        paused
          ? api.POST('/api/t/connections/{id}/pause', { params: { path: { id } } })
          : api.POST('/api/t/connections/{id}/resume', { params: { path: { id } } }),
      ),
    onSuccess: invalidate,
  });
}

/**
 * **Where the move of a personal account stands** (R2; D87, D92), for its card on the company's Connected
 * accounts. Asked only in a company's profile: in a person's own there is nothing to move.
 */
export function useMoveStatus(tenantId: string, id: string, enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: keys.move(tenantId, id),
    enabled,
    queryFn: () => unwrap(api.GET('/api/t/connections/{id}/move', { params: { path: { id } } })),
  });
}

/**
 * The three presses. Each invalidates the whole tenant: a press changes the account's own card, its state and
 * what it holds, as well as where the move stands. `access` is the list the person was shown (G8). **A refused
 * press too** (F336): a refusal is most often the screen being out of date — the move finished or was cancelled in
 * another tab — so the card is read again and shows where the move now stands beside the refusal.
 */
export function useStartMove(tenantId: string) {
  const api = useApi();
  const invalidate = useInvalidateTenant(tenantId);
  return useMutation({
    mutationFn: ({ id, access }: { id: string; access: string[] }) =>
      unwrap(
        api.POST('/api/t/connections/{id}/move', { params: { path: { id } }, body: { access } }),
      ),
    onSettled: invalidate,
  });
}

export function useFinishMove(tenantId: string) {
  const api = useApi();
  const invalidate = useInvalidateTenant(tenantId);
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(api.POST('/api/t/connections/{id}/move/finish', { params: { path: { id } } })),
    onSettled: invalidate,
  });
}

export function useCancelMove(tenantId: string) {
  const api = useApi();
  const invalidate = useInvalidateTenant(tenantId);
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(api.POST('/api/t/connections/{id}/move/cancel', { params: { path: { id } } })),
    onSettled: invalidate,
  });
}

export function useBankBalances(tenantId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.bank(tenantId),
    queryFn: () => unwrap(api.GET('/api/t/bank/balances')),
  });
}

export function useAttention(tenantId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.attention(tenantId),
    queryFn: () => unwrap(api.GET('/api/t/me/attention')),
  });
}

/**
 * **Older** (F516): the lines on the list more than a month, by the same rules, found by words when two or more are
 * typed. Read only while the screen shows it.
 */
export function useOlderAttention(tenantId: string, query: string, enabled: boolean) {
  const api = useApi();
  const words = query.trim();
  const q = words.length >= 2 ? words : '';
  return useQuery({
    queryKey: keys.attentionOlder(tenantId, q),
    queryFn: () =>
      unwrap(
        api.GET('/api/t/me/attention', {
          params: { query: { view: 'older', ...(q.length > 0 ? { q } : {}) } },
        }),
      ),
    enabled,
  });
}

/** One press brings a line back from *Older* for another month. The record is not touched (F516). */
export function useBringBack(tenantId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { subject: 'commitment' | 'person'; id: string }) => {
      const { data, error, response } = await api.POST('/api/t/me/attention/bring-back', {
        body: input,
      });
      if (error || !response.ok) throw await failure(response, error, 'bring_back_failed');
      return data;
    },
    // Today's list and *Older* both: the line leaves one and joins the other.
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: keys.attention(tenantId) });
    },
  });
}

export function usePeople(tenantId: string, query: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.people(tenantId, query),
    queryFn: () =>
      unwrap(
        api.GET('/api/t/me/people', {
          params: { query: { ...(query.length >= 2 ? { query } : {}), limit: 100 } },
        }),
      ),
  });
}

export type MeMergeProposal = Schemas['MeMergeProposal'];
type PersonPairs = { a: string; b: string }[];

/**
 * **The owner's word on who is who** (one card per person; F379): *same person*, *not the same*, *not this person*.
 * Each changes which cards there are, so the whole profile is read again — Needs you included, which names people.
 */
export function useMergePeople(tenantId: string) {
  const api = useApi();
  const invalidate = useInvalidateTenant(tenantId);
  return useMutation({
    mutationFn: (pairs: PersonPairs) =>
      unwrap(api.POST('/api/t/me/people/merge', { body: { pairs } })),
    onSuccess: invalidate,
  });
}

export function useRefusePeople(tenantId: string) {
  const api = useApi();
  const invalidate = useInvalidateTenant(tenantId);
  return useMutation({
    mutationFn: async (pairs: PersonPairs) => {
      const { error, response } = await api.POST('/api/t/me/people/not-same', { body: { pairs } });
      if (error || !response.ok) throw await failure(response, error, 'not_same_failed');
    },
    onSuccess: invalidate,
  });
}

export function useSeparatePerson(tenantId: string) {
  const api = useApi();
  const invalidate = useInvalidateTenant(tenantId);
  return useMutation({
    mutationFn: async (id: string) => {
      const { error, response } = await api.POST('/api/t/me/people/{id}/separate', {
        params: { path: { id } },
      });
      if (error || !response.ok) throw await failure(response, error, 'separate_failed');
    },
    onSuccess: invalidate,
  });
}

export function useCommitments(tenantId: string, state: 'open' | 'dismissed') {
  const api = useApi();
  return useQuery({
    queryKey: keys.commitments(tenantId, state),
    queryFn: () =>
      unwrap(api.GET('/api/t/me/commitments', { params: { query: { state, limit: 100 } } })),
  });
}

/** The person's verdict on a commitment, which no re-derivation undoes. */
/**
 * *Not now* for one person's gone-quiet item, and *tell me again* to undo it.
 *
 * It invalidates the same two lists as a commitment verdict, and for the same reason: the item leaves the
 * attention list, and the person's own row now carries an end date a screen can show. What it does **not**
 * take is a length — the API does not offer one (Batch F.3).
 */
export function useSnoozePerson(tenantId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, on }: { id: string; on: boolean }) => {
      const { data, error, response } = await api.PUT('/api/t/me/people/{id}/snooze', {
        params: { path: { id } },
        body: { on },
      });
      if (error || !response.ok) throw await failure(response, error, 'snooze_failed');
      return data;
    },
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: keys.attention(tenantId) });
      await qc.invalidateQueries({ queryKey: ['t', tenantId, 'people'] });
    },
  });
}

export function useDecideCommitment(tenantId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, state }: { id: string; state: 'open' | 'done' | 'dismissed' }) => {
      // A 204 carries no body, which `unwrap` would read as a failure: the status is the answer here.
      const { error, response } = await api.PUT('/api/t/me/commitments/{id}/state', {
        params: { path: { id } },
        body: { state },
      });
      if (error || !response.ok) throw await failure(response, error, 'commitment_failed');
    },
    // Both lists, and what needs you: a commitment forgotten leaves one and joins the other, and stops
    // being a reason to open the app.
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: ['t', tenantId, 'commitments'] });
      await qc.invalidateQueries({ queryKey: keys.attention(tenantId) });
    },
  });
}

export function useEngine(tenantId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.engine(tenantId),
    queryFn: () => unwrap(api.GET('/api/t/engine')),
    // The day's spend and the queue move without anybody clicking. The same cadence as Clarify, for the
    // same reason: a status screen that is stale is a status screen that is wrong.
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
  });
}

/** The asker's own Chat threads in the profile on screen, latest first (Chat slices 2 and 6). */
export function useChatThreads(tenantId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.chatThreads(tenantId),
    queryFn: async () => (await unwrap(api.GET('/api/t/chat/threads'))).threads,
  });
}

export function useChatThread(tenantId: string, threadId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: keys.chatThread(tenantId, threadId ?? ''),
    enabled: threadId !== null,
    queryFn: () =>
      unwrap(api.GET('/api/t/chat/threads/{id}', { params: { path: { id: threadId ?? '' } } })),
  });
}

/**
 * The thread's latest turn, read once a second while one is being answered (F435): the job writes its progress to the
 * turn as it goes, and this is how the screen shows it.
 */
export function useChatTurn(tenantId: string, threadId: string | null, answering: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: keys.chatTurn(tenantId, threadId ?? ''),
    enabled: threadId !== null && answering,
    refetchInterval: answering ? 1000 : false,
    queryFn: async () =>
      (
        await unwrap(
          api.GET('/api/t/chat/threads/{id}/turn', { params: { path: { id: threadId ?? '' } } }),
        )
      ).turn,
  });
}

export function useOpenChatThread(tenantId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(api.POST('/api/t/chat/threads', { body: { title: null } })),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.chatThreads(tenantId) }),
  });
}

export function useDeleteChatThread(tenantId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (threadId: string) =>
      unwrap(api.DELETE('/api/t/chat/threads/{id}', { params: { path: { id: threadId } } })),
    // The thread is gone: its own reads are dropped rather than asked again, which could only answer 404.
    onSuccess: (_, threadId) => {
      qc.removeQueries({ queryKey: keys.chatThread(tenantId, threadId) });
      qc.removeQueries({ queryKey: keys.chatTurn(tenantId, threadId) });
      return qc.invalidateQueries({ queryKey: keys.chatThreads(tenantId) });
    },
  });
}

/** Sets a thread's companies (D51 row 6, F489), and reads the thread again so the screen shows what was kept. */
export function useSetChatScope(tenantId: string, threadId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (scope: { entities: string[] | null; excluded: string[] }) =>
      unwrap(
        api.PUT('/api/t/chat/threads/{id}/scope', {
          params: { path: { id: threadId } },
          body: scope,
        }),
      ),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.chatThread(tenantId, threadId) }),
  });
}

/** Asks, and holds until the job has answered, refused or failed; the turn's progress is read beside it. */
export function useAskChat(tenantId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (v: { threadId: string; question: string; escalate?: boolean }) =>
      unwrap(
        api.POST('/api/t/chat/threads/{id}/ask', {
          params: { path: { id: v.threadId } },
          // A stronger model only when the person asks for one (D51 row 2).
          body: { question: v.question, ...(v.escalate ? { escalate: true } : {}) },
        }),
      ),
    onSettled: () => qc.invalidateQueries({ queryKey: ['t', tenantId, 'chat'] }),
  });
}

/** The profile's own model key, as the screen may see it: provider, where, caps. Never the key (Chat slice 7). */
export function useOwnKey(tenantId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.ownKey(tenantId),
    queryFn: () => unwrap(api.GET('/api/t/engine/key')),
  });
}

export function useEnterOwnKey(tenantId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: {
      provider: 'anthropic' | 'openai';
      key: string;
      residency: 'us' | 'eu';
      dailyCapNok: number;
      monthlyCapNok: number;
    }) => unwrap(api.PUT('/api/t/engine/key', { body })),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.ownKey(tenantId) }),
  });
}

export function useRemoveOwnKey(tenantId: string) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => unwrap(api.DELETE('/api/t/engine/key')),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.ownKey(tenantId) }),
  });
}

/** The next thirteen weeks of one company's cash, inferred from its books (M3 · Treasury). */
export function useCashForecast(tenantId: string, entityId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: keys.cashForecast(tenantId, entityId ?? ''),
    enabled: entityId !== null,
    queryFn: () =>
      unwrap(
        api.GET('/api/t/entities/{id}/cash-forecast', {
          params: { path: { id: entityId ?? '' } },
        }),
      ),
  });
}

export function useCounterparties(tenantId: string, entityId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: keys.counterparties(tenantId, entityId ?? ''),
    enabled: entityId !== null,
    queryFn: () =>
      unwrap(
        api.GET('/api/t/entities/{id}/counterparties', {
          params: { path: { id: entityId ?? '' } },
        }),
      ),
  });
}

export function useDocuments(tenantId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.documents(tenantId),
    queryFn: () => unwrap(api.GET('/api/t/documents')),
  });
}

export function useEraseDocument(tenantId: string) {
  const api = useApi();
  const invalidate = useInvalidateTenant(tenantId);
  return useMutation({
    mutationFn: ({ id, basis }: { id: string; basis: string }) =>
      unwrap(api.DELETE('/api/t/documents/{id}', { params: { path: { id } }, body: { basis } })),
    onSuccess: invalidate,
  });
}

export function useResolveClarify(tenantId: string) {
  const api = useApi();
  const invalidate = useInvalidateTenant(tenantId);
  return useMutation({
    mutationFn: ({
      id,
      decision,
    }: {
      id: string;
      decision: 'keep_current' | 'use_incoming' | 'skip';
    }) =>
      unwrap(
        api.POST('/api/t/clarify/{id}/resolve', { params: { path: { id } }, body: { decision } }),
      ),
    onSuccess: invalidate,
  });
}

export type MeRecord = Schemas['MeRecord'];
export type MeRecords = Schemas['MeRecords'];

/**
 * **One record screen, page after page** (Personal step 2): Conversations, Calendar or Files, newest first, read from
 * what the sync stored. Under the tenant's key, so connecting, pausing or forgetting an account, which invalidate
 * the tenant, refresh it too.
 */
export function useRecords(
  tenantId: string,
  surface: 'mail' | 'calendar' | 'drive',
  account: string | null,
) {
  const api = useApi();
  return useInfiniteQuery<MeRecords, Error, { pages: MeRecords[] }, readonly unknown[], number>({
    queryKey: keys.records(tenantId, surface, account),
    initialPageParam: 0,
    getNextPageParam: (last: MeRecords) => last.next ?? undefined,
    queryFn: ({ pageParam }) =>
      unwrap(
        api.GET('/api/t/me/records', {
          params: {
            query: {
              surface,
              limit: 50,
              ...(pageParam > 0 ? { offset: pageParam } : {}),
              ...(account ? { account } : {}),
            },
          },
        }),
      ),
  });
}

export interface AuditPage {
  events: AuditEvent[];
  nextBefore: string | null;
  /** Rows of the whole log visible only to the person they concern, and so not in `events` (D86). */
  withheld: number;
}

export interface AuditFilters {
  kind?: AuditEvent['kind'] | undefined;
  outcome?: AuditEvent['outcome'] | undefined;
  entityId?: string | undefined;
}

export function useAudit(tenantId: string, filters: AuditFilters) {
  const api = useApi();
  return useInfiniteQuery<
    AuditPage,
    Error,
    { pages: AuditPage[] },
    readonly unknown[],
    string | undefined
  >({
    queryKey: keys.audit(tenantId, filters),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last: AuditPage) => last.nextBefore ?? undefined,
    queryFn: ({ pageParam }) =>
      unwrap(
        api.GET('/api/t/audit', {
          params: {
            query: {
              limit: 50,
              ...(filters.kind ? { kind: filters.kind } : {}),
              ...(filters.outcome ? { outcome: filters.outcome } : {}),
              ...(filters.entityId ? { entityId: filters.entityId } : {}),
              ...(pageParam ? { before: pageParam } : {}),
            },
          },
        }),
      ),
  });
}

export function useAuditVerify(tenantId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.auditVerify(tenantId),
    queryFn: () => unwrap(api.GET('/api/t/audit/verify')),
  });
}

export function useConnections(tenantId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.connections(tenantId),
    queryFn: async () => (await unwrap(api.GET('/api/t/connections'))).connections,
  });
}

export function useVerifyConnection(tenantId: string) {
  const api = useApi();
  const invalidate = useInvalidateTenant(tenantId);
  return useMutation({
    mutationFn: (id: string) =>
      unwrap(api.POST('/api/t/connections/{id}/verify', { params: { path: { id } } })),
    onSuccess: invalidate,
  });
}

/**
 * **Saves the person's language** (F344; Thomas, 2026-10-09). With `keepalive`, so the request is not dropped when
 * the page goes away under it — a reload, or a full load of the app's own — and the server gets the choice either
 * way. What the answer does to the screen is the caller's (`LanguageProvider`): the screen switches on it, never
 * before it.
 */
export function useSaveLanguage() {
  const api = useApi();
  return useMutation({
    mutationFn: async (locale: 'en' | 'nb-NO') => {
      const { error, response } = await api.PUT('/api/t/me/locale', {
        body: { locale },
        keepalive: true,
      });
      if (error || !response.ok) throw await failure(response, error, 'locale_failed');
    },
  });
}

/**
 * **Which of its own companies an Org made before profiles is** (R1, D82): said once, by the owner. The membership
 * list carries the number, so it is refetched.
 */
export function useSetOrgNumber() {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (entityId: string) => {
      const { error, response } = await api.PUT('/api/t/org/orgnr', { body: { entityId } });
      if (error || !response.ok) throw await failure(response, error, 'orgnr_failed');
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.me }),
  });
}

// ── Agents, credentials and grants ───────────────────────────────────────────
// Three lifetimes, not one: rotating a credential leaves the grants standing, and revoking a grant leaves the
// credential authenticating and denied everything. The screen keeps them apart because the model does.

/**
 * Who is asking on the consent screen, and whether the address they brought is one they registered (O16).
 * `ask` is null while the arrival is still being parsed, so a broken URL never fires a request.
 */
/** One profile on the consent screen, as the API's own response describes it. */
export type ConsentProfile =
  paths['/api/mcp/consent']['get']['responses'][200]['content']['application/json']['profiles'][number];

/**
 * **The consent screen's question, per person** (D85): who is asking, whether the address they want the answer at is
 * one they registered, and every profile the person belongs to — Personal first — with the agents each has. The
 * person's, not a profile's, so the key carries no tenant.
 */
export function useMcpConsent(ask: { clientId: string; redirectUri: string } | null) {
  const api = useApi();
  return useQuery({
    queryKey: keys.mcpConsent(ask?.clientId ?? '', ask?.redirectUri ?? ''),
    enabled: ask !== null,
    queryFn: () =>
      unwrap(
        api.GET('/api/mcp/consent', {
          params: {
            query: {
              clientId: (ask as { clientId: string }).clientId,
              redirectUri: (ask as { redirectUri: string }).redirectUri,
            },
          },
        }),
      ),
  });
}

/**
 * The approval: one existing agent per chosen profile, one single-use code for the whole connection. Invalidates
 * nothing — the page leaves for the client's address the moment it has the code.
 */
export function useApproveConnection() {
  const api = useApi();
  return useMutation({
    mutationFn: (body: {
      clientId: string;
      redirectUri: string;
      codeChallenge: string;
      codeChallengeMethod: string;
      resource?: string;
      choices: { tenantId: string; agentId: string }[];
    }) => unwrap(api.POST('/api/mcp/authorize', { body })),
  });
}

export function useAgents(tenantId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.agents(tenantId),
    queryFn: async () => (await unwrap(api.GET('/api/t/agents'))).agents,
  });
}

function useAgentMutation<T, V>(
  tenantId: string,
  mutationFn: (api: ApiClient, variables: V) => Promise<T>,
) {
  const api = useApi();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (variables: V) => mutationFn(api, variables),
    onSuccess: () => qc.invalidateQueries({ queryKey: keys.agents(tenantId) }),
  });
}

export function useCreateAgent(tenantId: string) {
  return useAgentMutation(tenantId, (api, body: { name: string; version: string }) =>
    unwrap(api.POST('/api/t/agents', { body })),
  );
}

export function useSetAgentStatus(tenantId: string) {
  return useAgentMutation(
    tenantId,
    (api, v: { id: string; status: 'enabled' | 'disabled' | 'revoked' }) =>
      unwrap(
        api.PUT('/api/t/agents/{id}/status', {
          params: { path: { id: v.id } },
          body: { status: v.status },
        }),
      ),
  );
}

/** The secret comes back once. Nothing stores it; the screen shows it until the operator dismisses it. */
export function useIssueCredential(tenantId: string) {
  return useAgentMutation(tenantId, (api, id: string) =>
    unwrap(api.POST('/api/t/agents/{id}/credentials', { params: { path: { id } } })),
  );
}

export function useRevokeCredential(tenantId: string) {
  return useAgentMutation(tenantId, (api, v: { id: string; credentialId: string }) =>
    unwrap(
      api.POST('/api/t/agents/{id}/credentials/{credentialId}/revoke', {
        params: { path: { id: v.id, credentialId: v.credentialId } },
      }),
    ),
  );
}

export function useCreateGrant(tenantId: string) {
  return useAgentMutation(
    tenantId,
    (
      api,
      v: {
        id: string;
        entities: string[];
        resources: GrantResources;
        purpose: string;
        days?: number;
      },
    ) =>
      unwrap(
        api.POST('/api/t/agents/{id}/grants', {
          params: { path: { id: v.id } },
          body: {
            entities: v.entities,
            resources: v.resources,
            purpose: v.purpose,
            ...(v.days === undefined ? {} : { days: v.days }),
          },
        }),
      ),
  );
}

export function useRevokeGrant(tenantId: string) {
  return useAgentMutation(tenantId, (api, grantId: string) =>
    unwrap(api.POST('/api/t/grants/{id}/revoke', { params: { path: { id: grantId } } })),
  );
}

/** The sync runs of an entity's ledger, newest first, each with the crossing it made. */
export function useLedgerRuns(tenantId: string, entityId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.ledgerRuns(tenantId, entityId),
    queryFn: () =>
      unwrap(api.GET('/api/t/entities/{id}/ledger-runs', { params: { path: { id: entityId } } })),
  });
}

export function useBeliefs(tenantId: string, entityId: string | null, includeForgotten: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: keys.beliefs(tenantId, entityId ?? '', includeForgotten),
    enabled: entityId !== null,
    queryFn: async () =>
      (
        await unwrap(
          api.GET('/api/t/entities/{id}/beliefs', {
            params: {
              path: { id: entityId ?? '' },
              query: { includeForgotten: includeForgotten ? 'true' : 'false' },
            },
          }),
        )
      ).beliefs,
  });
}

function useBeliefMutation<T, V>(
  tenantId: string,
  mutationFn: (api: ApiClient, variables: V) => Promise<T>,
) {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: V) => mutationFn(api, variables),
    onSuccess: () =>
      client.invalidateQueries({ queryKey: ['t', tenantId, 'beliefs'], exact: false }),
  });
}

export function useRecordBelief(tenantId: string) {
  return useBeliefMutation(
    tenantId,
    (
      api,
      v: {
        entityId: string;
        statement: string;
        beliefType: Belief['beliefType'];
        corrects?: string;
      },
    ) =>
      unwrap(
        api.POST('/api/t/entities/{id}/beliefs', {
          params: { path: { id: v.entityId } },
          body: {
            statement: v.statement,
            beliefType: v.beliefType,
            ...(v.corrects ? { corrects: v.corrects } : {}),
          },
        }),
      ),
  );
}

export function useForgetBelief(tenantId: string) {
  return useBeliefMutation(tenantId, (api, v: { id: string }) =>
    unwrap(api.POST('/api/t/beliefs/{id}/forget', { params: { path: { id: v.id } } })),
  );
}

/**
 * **A person's own beliefs** (Personal step 3; 0104): the same three acts in their own profile, which names no
 * company. Keyed under `beliefs` with the profile as the subject, so the shared mutation helper refreshes them.
 */
export function useMeBeliefs(tenantId: string, includeForgotten: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: keys.beliefs(tenantId, 'own', includeForgotten),
    queryFn: async () =>
      (
        await unwrap(
          api.GET('/api/t/me/beliefs', {
            params: { query: { includeForgotten: includeForgotten ? 'true' : 'false' } },
          }),
        )
      ).beliefs,
  });
}

export function useRecordMeBelief(tenantId: string) {
  return useBeliefMutation(
    tenantId,
    (api, v: { statement: string; beliefType: Belief['beliefType']; corrects?: string }) =>
      unwrap(api.POST('/api/t/me/beliefs', { body: v })),
  );
}

export function useForgetMeBelief(tenantId: string) {
  return useBeliefMutation(tenantId, (api, v: { id: string }) =>
    unwrap(api.POST('/api/t/me/beliefs/{id}/forget', { params: { path: { id: v.id } } })),
  );
}

// ─── The policy engine (F369): the kill switch, the rules, the approvals ───────────────────────────────────

export function usePolicy(tenantId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.policy(tenantId),
    queryFn: () => unwrap(api.GET('/api/t/policy')),
  });
}

export function useApprovals(tenantId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.approvals(tenantId),
    queryFn: async () => (await unwrap(api.GET('/api/t/approvals'))).approvals,
  });
}

/** Every change here can move both the switch and what waits, so each one refreshes both. */
function usePolicyMutation<T, V>(
  tenantId: string,
  mutationFn: (api: ApiClient, variables: V) => Promise<T>,
) {
  const api = useApi();
  const client = useQueryClient();
  return useMutation({
    mutationFn: (variables: V) => mutationFn(api, variables),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: keys.policy(tenantId) });
      await client.invalidateQueries({ queryKey: keys.approvals(tenantId) });
      await client.invalidateQueries({ queryKey: ['t', tenantId, 'beliefs'], exact: false });
    },
  });
}

export function useSetHalt(tenantId: string) {
  return usePolicyMutation(
    tenantId,
    (api, v: { entityId: string | null; halted: boolean; reason: string }) =>
      unwrap(api.POST('/api/t/policy/halt', { body: v })),
  );
}

export function useProposeRule(tenantId: string) {
  return usePolicyMutation(tenantId, (api, v: { rule: string; bounds?: Record<string, unknown> }) =>
    unwrap(
      api.POST('/api/t/policy/rules', {
        body: {
          rule: v.rule,
          agentId: null,
          entityId: null,
          ...(v.bounds ? { bounds: v.bounds } : {}),
        },
      }),
    ),
  );
}

export function useApproveRule(tenantId: string) {
  return usePolicyMutation(tenantId, (api, v: { id: string }) =>
    unwrap(api.POST('/api/t/policy/rules/{id}/approve', { params: { path: { id: v.id } } })),
  );
}

export function useSwitchRuleOff(tenantId: string) {
  return usePolicyMutation(tenantId, (api, v: { id: string }) =>
    unwrap(api.POST('/api/t/policy/rules/{id}/off', { params: { path: { id: v.id } } })),
  );
}

export function useDecideApproval(tenantId: string) {
  return usePolicyMutation(tenantId, (api, v: { id: string; decision: 'approve' | 'reject' }) =>
    unwrap(
      api.POST('/api/t/approvals/{id}', {
        params: { path: { id: v.id } },
        body: { decision: v.decision },
      }),
    ),
  );
}

/** Deleting an agent's note for real (D70): its text is erased, and no read returns it again. */
export function useEraseAgentNote(tenantId: string) {
  return useBeliefMutation(tenantId, (api, v: { id: string }) =>
    unwrap(api.POST('/api/t/beliefs/{id}/erase', { params: { path: { id: v.id } } })),
  );
}

// ── Personal step 3: profile, assets and liabilities, by hand (F372) ─────────

export type MeProfile = Schemas['MeProfile'];
export type MeProfileInput = Schemas['MeProfileInput'];
export type MeHoldings = Schemas['MeHoldings'];
export type MeAsset = Schemas['MeAsset'];
export type MeLiability = Schemas['MeLiability'];
export type MeAssetInput = Schemas['MeAssetInput'];
export type MeAssetEdit = Schemas['MeAssetEdit'];
export type MeLiabilityInput = Schemas['MeLiabilityInput'];
export type MeLiabilityEdit = Schemas['MeLiabilityEdit'];
export type MeValueInput = Schemas['MeValueInput'];

export function useMeProfile(tenantId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.meProfile(tenantId),
    queryFn: () => unwrap(api.GET('/api/t/me/profile')),
  });
}

export function useSaveMeProfile(tenantId: string) {
  const api = useApi();
  const invalidate = useInvalidateTenant(tenantId);
  return useMutation({
    mutationFn: (body: MeProfileInput) => unwrap(api.PUT('/api/t/me/profile', { body })),
    onSuccess: invalidate,
  });
}

// ── Contacts, and a person's card (Personal build step 6) ────────────────────

export type MeContacts = Schemas['MeContacts'];
export type MePersonCard = Schemas['MePersonCard'];

/** The address book read now: what enriches a card, and who is in no conversation yet. */
export function useMeContacts(tenantId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.meContacts(tenantId),
    queryFn: () => unwrap(api.GET('/api/t/me/contacts')),
  });
}

export function usePersonCard(tenantId: string, id: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.personCard(tenantId, id),
    queryFn: () => unwrap(api.GET('/api/t/me/people/{id}/card', { params: { path: { id } } })),
  });
}

// ── A person's own bank (Personal build step 5) ──────────────────────────────

export type MePersonalBank = Schemas['MePersonalBank'];

/** The person's accounts read live, and the lines that repeat in them. */
export function useMeBank(tenantId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.meBank(tenantId),
    queryFn: () => unwrap(api.GET('/api/t/me/bank')),
  });
}

/** The banks that serve a person's own accounts, asked only when the person opens the connect. */
export function useMeBanks(tenantId: string, enabled: boolean) {
  const api = useApi();
  return useQuery({
    queryKey: keys.meBanks(tenantId),
    queryFn: () => unwrap(api.GET('/api/t/me/bank/banks')),
    enabled,
  });
}

export function useStartMeBankConnect() {
  const api = useApi();
  return useMutation({
    mutationFn: (body: { bank: string }) => unwrap(api.POST('/api/t/me/bank/authorize', { body })),
  });
}

export function useCompleteMeBankConnect(tenantId: string) {
  const api = useApi();
  const invalidate = useInvalidateTenant(tenantId);
  return useMutation({
    mutationFn: (body: { code?: string; state?: string; error?: string }) =>
      unwrap(api.POST('/api/t/me/bank/callback', { body })),
    onSuccess: invalidate,
  });
}

export function useJudgeCashflow(tenantId: string) {
  const api = useApi();
  const invalidate = useInvalidateTenant(tenantId);
  return useMutation({
    mutationFn: ({
      id,
      ...body
    }: {
      id: string;
      verdict: 'confirm' | 'forget';
      kind?: string | null;
    }) => unwrap(api.PUT('/api/t/me/cashflow/{id}', { params: { path: { id } }, body })),
    onSuccess: invalidate,
  });
}

// ── Goals and tasks (Personal build step 4) ──────────────────────────────────

export type MeGoals = Schemas['MeGoals'];
export type MeGoal = Schemas['MeGoal'];
export type MeGoalInput = Schemas['MeGoalInput'];
export type MeTasks = Schemas['MeTasks'];

export function useGoals(tenantId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.goals(tenantId),
    queryFn: () => unwrap(api.GET('/api/t/me/goals')),
  });
}

/** The person's open Todoist tasks, read now: asked only by a screen that shows them. */
export function useMeTasks(tenantId: string, enabled = true) {
  const api = useApi();
  return useQuery({
    queryKey: keys.meTasks(tenantId),
    queryFn: () => unwrap(api.GET('/api/t/me/tasks')),
    enabled,
  });
}

export function useAddGoal(tenantId: string) {
  const api = useApi();
  const invalidate = useInvalidateTenant(tenantId);
  return useMutation({
    mutationFn: (body: MeGoalInput) => unwrap(api.POST('/api/t/me/goals', { body })),
    onSuccess: invalidate,
  });
}

export function useSaveGoal(tenantId: string) {
  const api = useApi();
  const invalidate = useInvalidateTenant(tenantId);
  return useMutation({
    mutationFn: ({ id, ...body }: MeGoalInput & { id: string }) =>
      unwrap(api.PUT('/api/t/me/goals/{id}', { params: { path: { id } }, body })),
    onSuccess: invalidate,
  });
}

export function useLinkToGoal(tenantId: string) {
  const api = useApi();
  const invalidate = useInvalidateTenant(tenantId);
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string; commitment?: string; task?: string }) =>
      unwrap(api.POST('/api/t/me/goals/{id}/links', { params: { path: { id } }, body })),
    onSuccess: invalidate,
  });
}

export function useUnlinkFromGoal(tenantId: string) {
  const api = useApi();
  const invalidate = useInvalidateTenant(tenantId);
  return useMutation({
    mutationFn: ({ id, linkId }: { id: string; linkId: string }) =>
      unwrap(
        api.POST('/api/t/me/goals/{id}/links/{linkId}/end', { params: { path: { id, linkId } } }),
      ),
    onSuccess: invalidate,
  });
}

export function useHoldings(tenantId: string) {
  const api = useApi();
  return useQuery({
    queryKey: keys.holdings(tenantId),
    queryFn: () => unwrap(api.GET('/api/t/me/holdings')),
  });
}

export function useAddMeAsset(tenantId: string) {
  const api = useApi();
  const invalidate = useInvalidateTenant(tenantId);
  return useMutation({
    mutationFn: (body: MeAssetInput) => unwrap(api.POST('/api/t/me/assets', { body })),
    onSuccess: invalidate,
  });
}

export function useEditMeAsset(tenantId: string) {
  const api = useApi();
  const invalidate = useInvalidateTenant(tenantId);
  return useMutation({
    mutationFn: async ({ id, ...body }: MeAssetEdit & { id: string }) => {
      const { error, response } = await api.PATCH('/api/t/me/assets/{id}', {
        params: { path: { id } },
        body,
      });
      if (error || !response.ok) throw await failure(response, error, 'edit_failed');
    },
    onSuccess: invalidate,
  });
}

export function useEndMeAsset(tenantId: string) {
  const api = useApi();
  const invalidate = useInvalidateTenant(tenantId);
  return useMutation({
    mutationFn: async ({ id, endedOn }: { id: string; endedOn: string }) => {
      const { error, response } = await api.POST('/api/t/me/assets/{id}/end', {
        params: { path: { id } },
        body: { endedOn },
      });
      if (error || !response.ok) throw await failure(response, error, 'end_failed');
    },
    onSuccess: invalidate,
  });
}

export function useValueMeAsset(tenantId: string) {
  const api = useApi();
  const invalidate = useInvalidateTenant(tenantId);
  return useMutation({
    mutationFn: ({ id, ...body }: MeValueInput & { id: string }) =>
      unwrap(api.POST('/api/t/me/assets/{id}/values', { params: { path: { id } }, body })),
    onSuccess: invalidate,
  });
}

export function useAddMeLiability(tenantId: string) {
  const api = useApi();
  const invalidate = useInvalidateTenant(tenantId);
  return useMutation({
    mutationFn: (body: MeLiabilityInput) => unwrap(api.POST('/api/t/me/liabilities', { body })),
    onSuccess: invalidate,
  });
}

export function useEditMeLiability(tenantId: string) {
  const api = useApi();
  const invalidate = useInvalidateTenant(tenantId);
  return useMutation({
    mutationFn: async ({ id, ...body }: MeLiabilityEdit & { id: string }) => {
      const { error, response } = await api.PATCH('/api/t/me/liabilities/{id}', {
        params: { path: { id } },
        body,
      });
      if (error || !response.ok) throw await failure(response, error, 'edit_failed');
    },
    onSuccess: invalidate,
  });
}

export function useEndMeLiability(tenantId: string) {
  const api = useApi();
  const invalidate = useInvalidateTenant(tenantId);
  return useMutation({
    mutationFn: async ({ id, endedOn }: { id: string; endedOn: string }) => {
      const { error, response } = await api.POST('/api/t/me/liabilities/{id}/end', {
        params: { path: { id } },
        body: { endedOn },
      });
      if (error || !response.ok) throw await failure(response, error, 'end_failed');
    },
    onSuccess: invalidate,
  });
}

export function useValueMeLiability(tenantId: string) {
  const api = useApi();
  const invalidate = useInvalidateTenant(tenantId);
  return useMutation({
    mutationFn: ({ id, ...body }: MeValueInput & { id: string }) =>
      unwrap(api.POST('/api/t/me/liabilities/{id}/values', { params: { path: { id } }, body })),
    onSuccess: invalidate,
  });
}
